import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getDashboardStats, getOrganizations } from '../services/apiService';
import { LoadingSpinner } from './icons';
import { User, UserRole, Organization } from '../types';

interface DashboardStats {
  openIncidents: number;
  resolvedIncidents: number;
  totalDebts: number;
  incidentsByType: { name: string, value: number }[];
  incidentsByStatus: { name: string, value: number }[];
}

const COLORS = ['#0D47A1', '#1E88E5', '#64B5F6', '#FFC107', '#F44336'];

const StatCard: React.FC<{ title: string; value: string | number; }> = ({ title, value }) => (
  <div className="bg-white p-6 rounded-lg shadow-md">
    <h3 className="text-sm font-medium text-gray-500">{title}</h3>
    <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
  </div>
);

const DashboardPage: React.FC<{ user: User }> = ({ user }) => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');

    const isPlatformAdmin = user.role === UserRole.SUPER_ADMIN || user.role === UserRole.PLATFORM_ADMIN;

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                if (isPlatformAdmin) {
                    const orgs = await getOrganizations();
                    setOrganizations(orgs);
                }
                const data = await getDashboardStats(user, selectedOrgId);
                setStats(data);
            } catch (error) {
                console.error("Failed to fetch dashboard data", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [user, selectedOrgId]);

    if (loading) {
        return <div className="flex justify-center items-center h-full"><LoadingSpinner className="w-10 h-10 text-primary" /></div>;
    }

    if (!stats) {
        return <div className="text-center text-red-500">Failed to load dashboard data.</div>
    }

    const filteredOrganizations = organizations.filter(org => 
        org.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const selectedOrgName = selectedOrgId === 'all' 
        ? 'All Networks' 
        : organizations.find(o => o.id === selectedOrgId)?.name || '';

  return (
    <div>
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
                {isPlatformAdmin && <p className="text-gray-600">Viewing data for: <span className="font-semibold">{selectedOrgName}</span></p>}
            </div>
            {isPlatformAdmin && (
            <div className="flex flex-col sm:flex-row items-center gap-2 w-full md:w-auto">
                <input 
                    type="text"
                    placeholder="Search Network..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md focus:ring-primary-light focus:border-primary-light w-full"
                />
                <select 
                    value={selectedOrgId} 
                    onChange={e => setSelectedOrgId(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md focus:ring-primary-light focus:border-primary-light w-full"
                >
                    <option value="all">All Networks</option>
                    {filteredOrganizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                </select>
            </div>
        )}
      </div>
      
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard title="Open Incidents" value={stats.openIncidents} />
        <StatCard title="Total Resolved" value={stats.resolvedIncidents} />
        <StatCard title="Total Unresolved Debt" value={`$${stats.totalDebts.toLocaleString()}`} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Incidents by Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={stats.incidentsByType}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {stats.incidentsByType.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Incidents by Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.incidentsByStatus} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
              <XAxis dataKey="name" tick={{fontSize: 12}} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#1E88E5" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;